import React, { useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import ApiService from "../../api/apiService";
import showSwal from "../swal";
import { common } from "../../util/common";
import {
  convertTo12HourPlusDateFormat,
  formatToPeso,
} from "../../util/helperFunctions";
import FormsSearch from "./reusable/forms-search";
import "./request-main.css";
import ApprovalItemList from "./reusable/approval-item-list-history.jsx";

function ApprovalHistory() {
  const PAGE_SIZE = 8;

  const [requests, setRequests] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
  });

  const debounceRef = useRef(null);

  const mapRequests = (requests = []) => {
    return requests.map((request) => ({
      id: request.id,
      request_id: request.reference_id,
      request_type: request.request_type,
      link_id: request.request_link_id,
      total_cost: formatToPeso(request.total_cost),
      date: convertTo12HourPlusDateFormat(request.createdAt),
      requestor: request.user_detail.display_name,
    }));
  };

  const getMyApprovalHistory = async ({
    page = 1,
    pageSize = PAGE_SIZE,
    keyword = "",
    append = false,
  } = {}) => {
    try {
      if (append) {
        setIsFetchingMore(true);
      } else {
        setIsInitialLoading(true);
      }

      const response = await ApiService.forms.getMyApprovalHistoryV2(
        page,
        pageSize,
        keyword,
      );

      const { success, requests: apiRequests = [] } = response || {};

      if (!success) {
        showSwal(common.errors.GETTING_MY_APPROVAL_HISTORY_ERROR);
        setHasMore(false);
        return;
      }

      const formattedRequests = mapRequests(apiRequests);

      setRequests((prev) => {
        if (!append) return formattedRequests;

        const existingIds = new Set(prev.map((item) => item.id));
        const dedupedNewItems = formattedRequests.filter(
          (item) => !existingIds.has(item.id),
        );

        return [...prev, ...dedupedNewItems];
      });

      setHasMore(apiRequests.length === pageSize);
      setPagination((prev) => ({
        ...prev,
        page,
        pageSize,
      }));
    } catch (error) {
      console.error("Failed to fetch requests:", error);
      showSwal(common.errors.GETTING_MY_APPROVAL_HISTORY_ERROR);
      setHasMore(false);
    } finally {
      setIsInitialLoading(false);
      setIsFetchingMore(false);
    }
  };

  useEffect(() => {
    getMyApprovalHistory({
      page: 1,
      pageSize: PAGE_SIZE,
      keyword: "",
      append: false,
    });
  }, []);

  const fetchMoreData = async () => {
    if (isFetchingMore || !hasMore || isInitialLoading) return;

    await getMyApprovalHistory({
      page: pagination.page + 1,
      pageSize: pagination.pageSize,
      keyword: searchKeyword,
      append: true,
    });
  };

  const handleSearch = (query) => {
    const trimmedQuery = query.trim();

    setSearchKeyword(trimmedQuery);
    setHasMore(true);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setRequests([]);
      getMyApprovalHistory({
        page: 1,
        pageSize: PAGE_SIZE,
        keyword: trimmedQuery,
        append: false,
      });
    }, 400);
  };

  return (
    <div className="request-main">
      <div className="request-main-title">
        <div className="request-main-title__top">
          <h3>Approver History</h3>
        </div>

        <div className="request-main-title__search">
          <FormsSearch handleSearch={handleSearch} />
        </div>
      </div>

      {isInitialLoading ? (
        <div className="loader--container">
          <div className="loader--"></div>
        </div>
      ) : (
        <div
          id="request-scrollable-container"
          className="request-main---container"
        >
          <InfiniteScroll
            dataLength={requests.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={
              <div className="request-main__bottom-loader">
                <div className="loader-- small"></div>
                <span>Loading more requests...</span>
              </div>
            }
            endMessage={
              requests.length > 0 ? (
                <p className="request-main__end-message">
                  You’ve reached the end.
                </p>
              ) : null
            }
            scrollableTarget="request-scrollable-container"
            scrollThreshold="180px"
          >
            {requests.length > 0 ? (
              <div className="request-main__list">
                {requests.map((requestData) => (
                  <div className="request-main__item" key={requestData.id}>
                    <ApprovalItemList requestData={requestData} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="request-main__empty-state">
                <div className="request-main__empty-icon">📝</div>
                <h4>No requests found</h4>
                <p>
                  {searchKeyword
                    ? "Try changing your search keyword."
                    : "You don’t have any requests yet."}
                </p>
              </div>
            )}
          </InfiniteScroll>
        </div>
      )}
    </div>
  );
}

export default ApprovalHistory;
